import { Scope } from './enums/scope';
import { ISaveTenant, IValidationResult } from './models';
import { IToken } from './token';

const TRANSACTION_THRESHOLD_TIME_IN_SEC = 5;

export type BaseAuthorizationResponseType = {
	error: string;
	error_description: string;
};
export type AuthorizationResponseType = {
	response: Response;
} & BaseAuthorizationResponseType;

export function isAuthorizationResponseType(ob: unknown): ob is AuthorizationResponseType {
	return (
		(ob as AuthorizationResponseType).error !== undefined &&
		(ob as AuthorizationResponseType).error_description !== undefined &&
		(ob as AuthorizationResponseType).response !== undefined
	);
}

export function isValidationResult(validationResult: unknown): validationResult is IValidationResult {
	return (
		(validationResult as IValidationResult).isValid !== undefined &&
		Array.isArray((validationResult as IValidationResult).validationMessages) &&
		Array.isArray((validationResult as IValidationResult).warningMessages)
	);
}

export abstract class MetadataClient {
	private static _storageId: string;
	static get storageId(): string {
		return MetadataClient._storageId;
	}

	private static _tenant: string;
	static get tenant(): string {
		return MetadataClient._tenant;
	}

	static get hasTenant(): boolean {
		return MetadataClient.tenant !== null && MetadataClient.tenant !== undefined && MetadataClient.tenant.length > 0;
	}

	static get IsPublicToken(): 'true' | 'false' | undefined {
		return MetadataClient.getToken()?.isPublic as 'true' | 'false' | undefined;
	}

	public static onRequestLogin: () => void;
	public static onAccessDenied: () => void;
	public static onBadRequest: () => void;
	public static onTokenRequest: () => void;
	public static onSetupTwoFactorRequest: (userId, requirePassword, cancelable) => void;
	public static onDisableTwoFactorRequest: (userId) => void;
	public static onTokenChanged: (token: IToken) => void;

	static get endpointsUrl(): string {
		return '../';
	}

	protected static refreshTokenPromise: Promise<IToken>;

	public static initialize(storageId: string, tenant: string): void {
		MetadataClient._storageId = storageId;
		if (tenant) {
			MetadataClient._tenant = tenant.toLowerCase();
		}
	}

	protected static async handleError(response: Response): Promise<Response> {
		return new Promise<Response>((resolve, reject) => {
			if (!response.ok) {
				if (response.status === 401) {
					MetadataClient.reLogin();
					reject(response);
				} else if (response.status === 400) {
					response.json().then(
						(result: BaseAuthorizationResponseType) => {
							if (result.error === 'token_required') {
								MetadataClient.onTokenRequest();
							} else if (result.error === 'setup_twofactor') {
								MetadataClient.onSetupTwoFactorRequest(result.error_description, false, false);
							} else {
								MetadataClient.badRequest();
							}

							if (isValidationResult(result)) {
								reject(result);
								return;
							}

							reject(<AuthorizationResponseType>{
								error: result.error,
								error_description: result.error_description,
								response
							});
						},
						() => reject(response)
					);
				} else if (response.status === 404 || response.status === 403) {
					reject(response);
				} else if (response.status === 413) {
					reject(new Error('413: Request too large'));
				} else {
					try {
						response
							.json()
							.then((rJ) => {
								if (!rJ.message) {
									reject(new Error(`${response.status} - ${response.statusText}`));
								}
								reject(new Error(rJ.message));
							})
							.catch(() => reject(new Error(`${response.status} - ${response.statusText}`)));
					} catch (e) {
						if (e instanceof Error) {
							reject(new Error(e && e.message ? e.message : `${response.status} - ${response.statusText}`));
						}
					}
				}
			} else {
				resolve(response);
			}
		});
	}

	public static async isValidActiveDirectory(saveTenant: ISaveTenant): Promise<IValidationResult> {
		return MetadataClient.ajax<IValidationResult>(
			new Request(MetadataClient.endpointsUrl + `api/v1/activedirectory/isValid`, {
				headers: new Headers({ 'Content-Type': 'application/json; charset=utf-8' }),
				method: 'POST',
				body: JSON.stringify(saveTenant)
			})
		);
	}

	public static autoLoginActiveDirectory(code: string, tenant: string, clientId: string, redirectUri: string): Promise<void> {
		const headers = {
			Tenant: tenant,
			Code: code,
			RedirectUri: redirectUri
		};

		const request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token`, {
			headers: new Headers(headers),
			body: new URLSearchParams({
				grant_type: 'password',
				username: 'null',
				password: 'null',
				client_id: clientId
			}),
			method: 'POST'
		});

		return fetch(request)
			.then(MetadataClient.handleError)
			.then((r) => r.json())
			.then(MetadataClient.setToken);
	}

	public static autoLoginSaml(samlResponseId: string, tenant: string, clientId: string): Promise<void> {
		const headers = {
			Tenant: tenant,
			samlResponseId: samlResponseId,
			RedirectUri: window.location.href
		};
		const request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token`, {
			headers: new Headers(headers),
			body: new URLSearchParams({
				grant_type: 'password',
				username: 'null',
				password: 'null',
				client_id: clientId
			}),
			method: 'POST'
		});

		return fetch(request)
			.then(MetadataClient.handleError)
			.then((r) => r.json())
			.then(MetadataClient.setToken);
	}

	public static async publicLogin(clientId: string, appsPublicLanguage: string = null): Promise<void> {
		const body = new URLSearchParams({
			grant_type: 'password',
			client_id: clientId,
			scope: Scope.Public
		});

		if (appsPublicLanguage) {
			body.append('appsPublicLanguage', appsPublicLanguage);
		}

		return MetadataClient._login(body);
	}

	public static async login(userName: string, password: string, clientId: string, token?: string): Promise<void> {
		const body = new URLSearchParams({
			grant_type: 'password',
			username: userName,
			password: password,
			client_id: clientId
		});

		if (token) {
			body.append('token', token);
		}

		return MetadataClient._login(body);
	}

	private static async _login(body: URLSearchParams): Promise<void> {
		const headers: Record<string, any> = {};
		if (MetadataClient.tenant) {
			headers['Tenant'] = MetadataClient.tenant;
		}

		const request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token`, {
			headers: new Headers(headers),
			body: body,
			method: 'POST'
		});

		return fetch(request)
			.then(MetadataClient.handleError)
			.then((r) => {
				if (!(r as Response).bodyUsed) {
					return r.json();
				}
			})
			.then(MetadataClient.setToken);
	}

	public static logout(silent = false): void {
		const token = MetadataClient.getToken();
		window.localStorage.removeItem(MetadataClient.storageId);

		if (token != null && !silent) {
			MetadataClient.revokeRefreshToken(token.refresh_token, token.access_token);
		}
	}

	public static getToken(): IToken {
		return JSON.parse(window.localStorage.getItem(MetadataClient.storageId));
	}

	public static hasToken(): boolean {
		const token = window.localStorage.getItem(MetadataClient.storageId);
		return !(!token || 0 === token.length);
	}

	protected static refreshToken(refreshToken: string, clientId: string): Promise<IToken> {
		const headers = MetadataClient.tenant ? { Tenant: MetadataClient.tenant } : {};

		const request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token`, {
			headers: new Headers(headers),
			body: new URLSearchParams({
				grant_type: 'refresh_token',
				refresh_token: refreshToken,
				client_id: clientId
			}),
			method: 'POST'
		});

		return fetch(request)
			.then(MetadataClient.handleError)
			.then((r) => r.json() as Promise<IToken>);
	}

	protected static revokeRefreshToken(refreshToken: string, accessToken: string): Promise<IToken> {
		const request = new Request(`${MetadataClient.endpointsUrl}api/v1/oauth2/token/revoke`, {
			body: new URLSearchParams({
				grant_type: 'refresh_token',
				refresh_token: refreshToken
			}),
			method: 'POST'
		});

		return MetadataClient.ajaxWithAuthorization(request, accessToken);
	}

	public static ajax<T>(request: Request, anonymous = false, byPass = false): Promise<T> {
		if (anonymous) {
			return MetadataClient.ajaxWithoutAuthorization(request);
		}

		let promise: Promise<T>;
		const token = MetadataClient.getToken();

		if (!token || (token.initialRecharge === 'true' && !byPass)) {
			MetadataClient.requestLogin();
			// Can we do bettter than returning empty Promise?
			return Promise.resolve(null);
		}

		if (Date.now() >= token.expiration_date) {
			if (!MetadataClient.refreshTokenPromise) {
				MetadataClient.refreshTokenPromise = MetadataClient.refreshToken(token.refresh_token, token.client_id);
			}

			return MetadataClient.refreshTokenPromise
				.then((newToken: IToken) => {
					MetadataClient.setToken(newToken);
					promise = MetadataClient.ajaxWithAuthorization<T>(request, newToken.access_token);
				})
				.catch(() => {
					MetadataClient.reLogin();
				})
				.then(() => {
					delete MetadataClient.refreshTokenPromise;
					return promise;
				});
		}
		return MetadataClient.ajaxWithAuthorization(request, token.access_token);
	}

	public static isTokenValid(): Promise<boolean> {
		return new Promise((resolve, reject) => {
			const token = MetadataClient.getToken();
			if (!token || token.initialRecharge === 'true') {
				reject(false);
			}

			if (Date.now() >= token.expiration_date) {
				if (!MetadataClient.refreshTokenPromise) {
					MetadataClient.refreshTokenPromise = MetadataClient.refreshToken(token.refresh_token, token.client_id);
				}

				MetadataClient.refreshTokenPromise
					.then((newToken: IToken) => {
						MetadataClient.setToken(newToken);
						resolve(true);
					})
					.catch(() => {
						reject(false);
					})
					.then(() => {
						delete MetadataClient.refreshTokenPromise;
					});
			} else {
				resolve(true);
			}
		});
	}

	protected static ajaxWithAuthorization<T>(request: Request, accessToken: string): Promise<T> {
		request.headers.append('Authorization', 'Bearer '.concat(accessToken));
		return MetadataClient.ajaxWithoutAuthorization(request);
	}

	protected static ajaxWithoutAuthorization<T>(request: Request): Promise<T> {
		if (MetadataClient.tenant) {
			request.headers.append('Tenant', MetadataClient.tenant);
		}

		return fetch(request)
			.then(MetadataClient.handleError)
			.then((response) => {
				const json =
					response &&
					response.headers &&
					(response.headers.get('Content-Type') || '').toLowerCase().startsWith('application/json');

				if (json) {
					return response.json().catch((r) => r);
				}

				return response;
			});
	}

	protected static setToken(token: IToken): void {
		token.expiration_date = Date.now() + (token.expires_in - TRANSACTION_THRESHOLD_TIME_IN_SEC) * 1000;
		window.localStorage.setItem(MetadataClient.storageId, JSON.stringify(token));

		if (MetadataClient.onTokenChanged) {
			MetadataClient.onTokenChanged(token);
		}
	}

	protected static requestLogin(): void {
		if (MetadataClient.onRequestLogin) {
			MetadataClient.onRequestLogin();
		}
	}

	protected static accessDenied(): void {
		if (MetadataClient.onAccessDenied) {
			MetadataClient.onAccessDenied();
		}
	}

	protected static badRequest(): void {
		if (MetadataClient.onBadRequest) {
			MetadataClient.onBadRequest();
		}
	}

	protected static reLogin<T>(): Promise<T> {
		MetadataClient.logout();
		MetadataClient.requestLogin();
		return Promise.resolve(null);
	}
}
